/*
 * Copyright (c) 2023 SmartWifi - All Rights Reserved.
 *
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 * Proprietary and confidential. No warranty, explicit or implicit provided. In
 * no event will the author be liable for any claim or damages.
 */

import { useEffect, useState } from "react";
import { containerHeightToGridHeight, gridLayoutConstants } from "../layouts/LayoutPortalUtil";
import { isMobile } from "../../util/UserAgent";
import ReactGridLayout, { WidthProvider } from "react-grid-layout";
import captivePortalLayout from "./CaptivePortalLayout";

const CaptivePortal = ({
  layout,
  fields,
  selectedLayer,
  selectedId,
  preview = false,
  fullPreview = false,
  mini = false,
  dimension
}) => {
  // grid layout element depends on if width if provided
  const dimensionWidth = dimension?.width;
  const dimensionHeight = dimension?.height;
  const GridLayout = dimensionWidth == null ? WidthProvider(ReactGridLayout) : ReactGridLayout;

  // height state
  const [height, setHeight] = useState(dimensionHeight ?? window.innerHeight);

  useEffect(() => {
    // set dimension height if parent provided
    if (dimensionHeight != null) {
      setHeight(dimensionHeight);
      return;
    }

    // register height change callback
    const handleHeightChange = () => {
      const activeElementName = document.activeElement?.name;

      // ignore resize if mobile and keyboard out
      if (isMobile() && (activeElementName === "username" || activeElementName === "password" || activeElementName === "sms" || activeElementName === "smsPassword")) {
        return;
      }

      setHeight(window.innerHeight);
    }

    window.addEventListener("resize", handleHeightChange);
    return () => window.removeEventListener("resize", handleHeightChange);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dimensionHeight]);

  // return nothing if layout not provided
  if (layout == null) {
    return null;
  }

  // turn layouts static
  try {
    const foregroundLayout = layout.foreground.template.map(item => ({...item, static: true}));
    const framingLayout = layout.framing.template.map(item => ({...item, static: true}));
    const heightStyle = { height: containerHeightToGridHeight(height) };
    const gridLayoutProps = gridLayoutConstants(height);

    return (
      <div className="fill-container d-flex align-items-center">
        <div className="w-100 layer-container" style={heightStyle}>
          <GridLayout
            className="layout no-animation background-layer no-click"
            layout={framingLayout}
            width={dimensionWidth}
            {...gridLayoutProps}
          >
            {captivePortalLayout({
              template: framingLayout,
              content: layout.framing.content,
              rowHeight: gridLayoutProps.rowHeight,
              fields: fields,
              selectedId: selectedLayer === "framing" && selectedId,
              preview: preview,
              fullPreview: fullPreview
            })}
          </GridLayout>
          <GridLayout
            className="layout no-animation foreground-layer"
            layout={foregroundLayout}
            width={dimensionWidth}
            {...gridLayoutProps}
          >
            {captivePortalLayout({
              template: foregroundLayout,
              content: layout.foreground.content,
              rowHeight: gridLayoutProps.rowHeight,
              fields: fields,
              selectedId: selectedLayer === "foreground" && selectedId,
              preview: preview,
              fullPreview: fullPreview,
              mini: mini
            })}
          </GridLayout>
        </div>
      </div>
    );
  } catch (error) {
    return null;
  }
};

export default CaptivePortal;
